import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { getNames } from 'i18n-iso-countries';
import { Subscription } from 'rxjs';

import { FormDropdownComponent } from '~/component/form/dropdown/dropdown.component';
import { DropdownOption } from '~/component/form/dropdown/interfaces/option.interface';
import { LinkedList } from '~/core/classes/linked-list';
import { getLangFromLocale } from '~/core/helpers/locale/locale.helper';
import { diacriticSort } from '~/core/helpers/sort/diacritic-sort.helper';

@Component({
  selector: 'app-form-dropdown-country',
  templateUrl: '../dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FormDropdownCountryComponent,
      multi: true,
    },
  ],
})
export class FormDropdownCountryComponent
  extends FormDropdownComponent<string>
  implements OnInit, OnDestroy
{
  @Input() public placeholder?: string;
  @Output() public selectedCountry? = new EventEmitter<string>();

  private subscription$: Subscription;

  constructor(
    private readonly translateService: TranslateService,
    dropdownComponent: ElementRef,
    @Inject(DOCUMENT) document: Document
  ) {
    super(dropdownComponent, document);
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscription$.unsubscribe();
  }

  public ngOnInit(): void {
    this.linkedListOption = new LinkedList<DropdownOption>([]);

    this.subscription$ = this.translateService.onLangChange.subscribe(
      (event: LangChangeEvent) => this.initComponent(event.lang)
    );

    this.initComponent(this.translateService.currentLang);
    super.ngOnInit();
  }

  private initComponent(locale: string): void {
    this.loadCountryList(getLangFromLocale(locale));

    if (this.placeholder) {
      this.options = [
        {
          disabled: true,
          selected: false,
          text: this.placeholder,
          value: '',
        },
        ...this.options,
      ];
    }
  }

  private loadCountryList(locale: string): void {
    this.options = [
      ...Object.entries(getNames(locale))
        .sort(([_key, _value], [_nextKey, _nextValue]) => {
          // Some countries can have several names, so we take arbitrarily the first one //
          if (Array.isArray(_value)) {
            _value = _value[0];
          }

          if (Array.isArray(_nextValue)) {
            _nextValue = _nextValue[0];
          }

          return diacriticSort(_value, _nextValue);
        })
        .map(([_key, _value]) => {
          return {
            disabled: false,
            selected: false,
            text: typeof _value === 'string' ? _value : _value[0],
            value: _key,
          };
        }),
    ];
  }

  public setOption(option: DropdownOption, close = true): void {
    super.setOption(option, close);
    this.selectedCountry.emit(option.value);
  }

  public writeValue(value: string): void {
    super.writeValue(value);
    this.selectedCountry.emit(value);
  }
}
